// frontend/src/features/portfolio-diversification-optimizer-pro/components/ExecutiveKpiHeader.tsx
//
// Executive scorecard: a metadata sub-bar, six institutional headline KPI cards
// (composite Diversification Score, Meucci Effective Number of Bets, Choueifaty
// Diversification Ratio, annualized volatility vs. ERC/HRP benchmarks, 95% daily
// Expected Shortfall + max drawdown, and normal vs. tail correlation contagion),
// followed by the categorized InsightPro cards with actionable takeaways.
//
// Themed for the shared light/dark dashboard shell.

import React from "react";
import {
  Activity,
  AlertTriangle,
  Award,
  CheckCircle2,
  Compass,
  GitBranch,
  Info,
  Layers,
  ShieldAlert,
  Sparkles,
} from "lucide-react";

import type { DiversificationProResult, InsightPro } from "../types";

interface ExecutiveKpiHeaderProps {
  data: DiversificationProResult;
}

function getScoreBadgeTheme(score: number) {
  if (score >= 75) {
    return {
      ring: "ring-emerald-500/40",
      bg: "bg-emerald-500/10",
      text: "text-emerald-600 dark:text-emerald-400",
      bar: "bg-emerald-500",
    };
  }
  if (score >= 55) {
    return {
      ring: "ring-sky-500/40",
      bg: "bg-sky-500/10",
      text: "text-sky-600 dark:text-sky-400",
      bar: "bg-sky-500",
    };
  }
  if (score >= 35) {
    return {
      ring: "ring-amber-500/40",
      bg: "bg-amber-500/10",
      text: "text-amber-600 dark:text-amber-400",
      bar: "bg-amber-500",
    };
  }
  return {
    ring: "ring-rose-500/40",
    bg: "bg-rose-500/10",
    text: "text-rose-600 dark:text-rose-400",
    bar: "bg-rose-500",
  };
}

function getInsightStyle(insight: InsightPro) {
  if (insight.severity === "warning") {
    return {
      border: "border-amber-500/30",
      bg: "bg-amber-500/5",
      icon: <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />,
      badge: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    };
  }
  if (insight.severity === "positive") {
    return {
      border: "border-emerald-500/30",
      bg: "bg-emerald-500/5",
      icon: <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500 dark:text-emerald-400" />,
      badge: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    };
  }
  return {
    border: "border-indigo-500/30",
    bg: "bg-indigo-500/5",
    icon: <Info className="h-4 w-4 shrink-0 text-indigo-500 dark:text-indigo-400" />,
    badge: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  };
}

const cardClass =
  "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/90";
const cardLabelClass =
  "flex items-center justify-between text-xs font-medium text-slate-500 dark:text-slate-400";
const bigValueClass = "font-mono text-3xl font-bold text-slate-900 dark:text-white";
const subTextClass = "mt-2 text-[11px] text-slate-500 dark:text-slate-400";

export const ExecutiveKpiHeader: React.FC<ExecutiveKpiHeaderProps> = ({ data }) => {
  const theme = getScoreBadgeTheme(data.diversification_score);
  const nAssets = data.tickers.length;
  const contagionSpread = data.correlation_analysis.tail_contagion_spread;

  return (
    <div className="space-y-6">
      {/* Metadata Sub-bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white/60 px-4 py-2.5 text-xs text-slate-500 dark:border-slate-800/80 dark:bg-slate-900/60 dark:text-slate-400">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>
            Universe:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {nAssets} Holdings
            </strong>
          </span>
          <span className="hidden sm:inline">•</span>
          <span>
            Aligned Trading Days:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {data.observations.toLocaleString()}
            </strong>
          </span>
          {data.period_start && data.period_end && (
            <>
              <span className="hidden sm:inline">•</span>
              <span>
                Sample Window:{" "}
                <strong className="font-mono text-slate-700 dark:text-slate-200">
                  {data.period_start} → {data.period_end}
                </strong>
              </span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            Ledoit-Wolf Shrinkage δ:{" "}
            <strong className="font-mono text-indigo-600 dark:text-indigo-400">
              {(data.correlation_analysis.shrinkage_intensity * 100).toFixed(1)}%
            </strong>
          </span>
          <span className="hidden sm:inline">•</span>
          <span>
            Clusters Detected:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {data.n_clusters}
            </strong>
          </span>
        </div>
      </div>

      {/* 6 Institutional Headline KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {/* Card 1: Composite Diversification Score */}
        <div className={cardClass}>
          <div className={cardLabelClass}>
            <span>Diversification Score</span>
            <Award className={`h-4 w-4 ${theme.text}`} />
          </div>
          <div className="mt-2 flex items-baseline justify-between gap-2">
            <span className={bigValueClass}>{data.diversification_score.toFixed(1)}</span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${theme.bg} ${theme.text} ${theme.ring}`}
            >
              {data.diversification_label}
            </span>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className={`h-full rounded-full ${theme.bar}`}
              style={{ width: `${Math.min(100, data.diversification_score)}%` }}
            />
          </div>
        </div>

        {/* Card 2: Meucci Effective Number of Bets */}
        <div className={cardClass}>
          <div className={cardLabelClass}>
            <span>Effective Bets (Meucci)</span>
            <Layers className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className={bigValueClass}>{data.effective_number_of_bets.toFixed(2)}</span>
            <span className="font-mono text-xs text-slate-400 dark:text-slate-500">
              / {nAssets} assets
            </span>
          </div>
          <p className={subTextClass}>
            PCA entropy breadth:{" "}
            <strong className="text-slate-700 dark:text-slate-200">
              {((data.effective_number_of_bets / Math.max(nAssets, 1)) * 100).toFixed(0)}%
            </strong>{" "}
            orthogonal utilization
          </p>
        </div>

        {/* Card 3: Choueifaty Diversification Ratio */}
        <div className={cardClass}>
          <div className={cardLabelClass}>
            <span>Diversification Ratio</span>
            <Compass className="h-4 w-4 text-sky-500 dark:text-sky-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className={bigValueClass}>{data.diversification_ratio.toFixed(2)}x</span>
          </div>
          <p className={subTextClass}>
            Vol reduction vs. standalone:{" "}
            <strong className="text-emerald-600 dark:text-emerald-400">
              -{((1 - 1 / Math.max(data.diversification_ratio, 1)) * 100).toFixed(1)}%
            </strong>
          </p>
        </div>

        {/* Card 4: Annualized Volatility vs Benchmarks */}
        <div className={cardClass}>
          <div className={cardLabelClass}>
            <span>Annualized Volatility</span>
            <Activity className="h-4 w-4 text-violet-500 dark:text-violet-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className={bigValueClass}>
              {(data.portfolio_volatility * 100).toFixed(2)}%
            </span>
          </div>
          <p className={subTextClass}>
            HRP:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {(data.hrp_portfolio_volatility * 100).toFixed(1)}%
            </strong>{" "}
            | ERC:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {(data.erc_portfolio_volatility * 100).toFixed(1)}%
            </strong>
          </p>
        </div>

        {/* Card 5: 95% Daily Expected Shortfall (CVaR) & Max DD */}
        <div className={cardClass}>
          <div className={cardLabelClass}>
            <span>Daily 95% CVaR (Tail)</span>
            <ShieldAlert className="h-4 w-4 text-rose-500 dark:text-rose-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-mono text-3xl font-bold text-rose-600 dark:text-rose-400">
              -{(data.cvar_95_daily * 100).toFixed(2)}%
            </span>
          </div>
          <p className={subTextClass}>
            Historical Max Drawdown:{" "}
            <strong className="font-mono text-rose-500 dark:text-rose-300">
              {(data.max_drawdown * 100).toFixed(1)}%
            </strong>
          </p>
        </div>

        {/* Card 6: Normal vs. Tail Correlation Contagion */}
        <div className={cardClass}>
          <div className={cardLabelClass}>
            <span>Avg Pairwise Corr</span>
            <GitBranch className="h-4 w-4 text-amber-500 dark:text-amber-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className={bigValueClass}>{data.average_correlation.toFixed(2)}</span>
            <span className="font-mono text-xs text-slate-400 dark:text-slate-500">
              (Tail: {data.tail_correlation.toFixed(2)})
            </span>
          </div>
          <p className={subTextClass}>
            Contagion Spread:{" "}
            <strong
              className={`font-mono ${
                contagionSpread > 0.1
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-emerald-600 dark:text-emerald-400"
              }`}
            >
              {contagionSpread >= 0 ? "+" : ""}
              {contagionSpread.toFixed(2)}
            </strong>
          </p>
        </div>
      </div>

      {/* Categorized Executive Insights Strip */}
      {data.insights.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.insights.map((ins, idx) => {
            const style = getInsightStyle(ins);
            return (
              <div
                key={`${ins.title}-${idx}`}
                className={`flex flex-col justify-between rounded-xl border p-4 ${style.border} ${style.bg}`}
              >
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {style.icon}
                      <h4 className="text-xs font-semibold text-slate-800 dark:text-white">
                        {ins.title}
                      </h4>
                    </div>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${style.badge}`}
                    >
                      {ins.category.replace("_", " ")}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                    {ins.message}
                  </p>
                </div>

                {ins.actionable_takeaway && (
                  <div className="mt-3 flex items-start gap-1.5 border-t border-slate-200/80 pt-2.5 text-[11px] text-indigo-700 dark:border-slate-800/80 dark:text-indigo-300">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-500 dark:text-indigo-400" />
                    <span>{ins.actionable_takeaway}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
